import { getAuthToken } from '../../Api/authToken';
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL, BASE_URL } from '../../Api/api';
import { FiInbox, FiEye, FiTrash2, FiEdit2, FiMapPin, FiUserX, FiUserCheck, FiHome, FiHeart, FiX, FiSearch } from 'react-icons/fi';
import UserAvatar, { userAvatarUrl } from '../Components/UserAvatar';
import './AdminPages.css';

/**
 * Resolves the most accurate preferred location label for a user.
 * Priority: selected_city.city_name → preferred_location text → location text → '—'
 */
const resolveLocation = (u) => {
  // Backend may join and return the city object directly
  if (u.selected_city?.city_name) return u.selected_city.city_name;
  // Or the backend may flatten it into preferred_location
  if (u.preferred_location && u.preferred_location.trim()) return u.preferred_location;
  // Fallback: free-text location field
  if (u.location && u.location.trim()) return u.location;
  return null;
};

const LOCALE_LABELS = {
  en: 'English', ta: 'தமிழ்', hi: 'हिन्दी', te: 'తెలుగు', bn: 'বাংলা',
  mr: 'मराठी', gu: 'ગુજરાતી', kn: 'ಕನ್ನಡ', ml: 'മലയാളം', pa: 'ਪੰਜਾਬੀ',
  or: 'ଓଡ଼ିଆ', as: 'অসমীয়া', ur: 'اردو',
};

const resolveLocaleLabel = (code) => {
  if (!code) return '—';
  const c = String(code).toLowerCase();
  return LOCALE_LABELS[c] || c.toUpperCase();
};

const STATUS_LABELS = {
  1: { label: 'Pending', color: '#f59e0b', bg: '#FEF3C7' },
  2: { label: 'Approved', color: '#16a34a', bg: '#DCFCE7' },
  3: { label: 'Rejected', color: '#dc2626', bg: '#FEE2E2' },
};

const formatPrice = (item) => {
  if (item.is_project && (item.price_from || item.price_to)) {
    return `₹${(item.price_from || 0).toLocaleString('en-IN')} – ₹${(item.price_to || 0).toLocaleString('en-IN')}`;
  }
  if (item.price > 0) return `₹${item.price.toLocaleString('en-IN')}`;
  if (item.price_from || item.price_to) {
    return `₹${(item.price_from || 0).toLocaleString('en-IN')} – ₹${(item.price_to || 0).toLocaleString('en-IN')}`;
  }
  return 'Price on request';
};

const propertyImgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const p = String(path).replace(/^\/+/, '');
  return `${BASE_URL}/${p.startsWith('uploads/') ? p : `uploads/${p}`}`;
};

const propertyTitle = (item) =>
  item.location || item.address || item.seo_title || item.map_location_name || `Listing #${item.property_id || item.id}`;

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const [viewingUser, setViewingUser] = useState(null);
  const [propertiesUser, setPropertiesUser] = useState(null);
  const [userProperties, setUserProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [wishlistUser, setWishlistUser] = useState(null);
  const [wishlistData, setWishlistData] = useState({ items: [], activities: [] });
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    user_name: '',
    email: '',
    user_phone: '',
    password: '',
    is_active: true,
    can_post_listings: true,
    skip_kyc: false,
    default_listing_role: 'owner',
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  const openPhotoPreview = (user) => {
    if (!user?.profile || !userAvatarUrl(user.profile)) return;
    setPhotoPreview(user);
  };

  const closePhotoPreview = () => setPhotoPreview(null);

  useEffect(() => {
    if (!photoPreview) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePhotoPreview();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [photoPreview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      if (editingUser) {
        await axios.put(`${API_URL}/admin/users/${editingUser.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User created successfully');
      }
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save user');
    }
  };

  const emptyForm = {
    user_name: '',
    email: '',
    user_phone: '',
    password: '',
    is_active: true,
    can_post_listings: true,
    skip_kyc: false,
    chat_access: true,
    default_listing_role: 'owner',
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ ...emptyForm });
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      user_name: user.user_name || '',
      email: user.email || '',
      user_phone: user.user_phone || '',
      password: '',
      is_active: user.is_active !== undefined ? user.is_active : true,
      can_post_listings: user.can_post_listings !== false,
      skip_kyc: Boolean(user.skip_kyc),
      chat_access: user.chat_access !== false,
      default_listing_role: user.default_listing_role === 'agent' ? 'agent' : 'owner',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const toggleChatAccess = async (user, nextVal) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/admin/users/${user.id}`, { chat_access: nextVal }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prev) => prev.map((row) => (row.id === user.id ? { ...row, chat_access: nextVal } : row)));
      toast.success(nextVal ? 'Chat access enabled' : 'Chat access turned off');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update chat access');
    }
  };

  const handleToggleActive = async (user) => {
    const nextActive = user.is_active === false;
    const actionLabel = nextActive ? 'reactivate' : 'deactivate';
    if (!window.confirm(`${nextActive ? 'Activate' : 'Deactivate'} account for ${user.user_name}?`)) return;
    try {
      const token = getAuthToken();
      const res = await axios.patch(`${API_URL}/admin/users/${user.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers((prev) => prev.map((u) => (
        u.id === user.id ? { ...u, is_active: res.data.is_active } : u
      )));
      toast.success(`User ${actionLabel}d successfully`);
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${actionLabel} user`);
    }
  };

  const openUserProperties = async (user) => {
    setPropertiesUser(user);
    setUserProperties([]);
    setPropertiesLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/listings`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          user_id: user.id,
          page: 1,
          limit: Math.min(Math.max(Number(user.propertyCount) || 10, 10), 200),
        },
      });
      setUserProperties(res.data?.listings || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load user properties');
      setPropertiesUser(null);
    } finally {
      setPropertiesLoading(false);
    }
  };

  const closeUserProperties = () => {
    setPropertiesUser(null);
    setUserProperties([]);
  };

  const openUserWishlist = async (user) => {
    setWishlistUser(user);
    setWishlistData({ items: [], activities: [] });
    setWishlistLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/users/${user.id}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWishlistData({
        items: res.data?.items || [],
        activities: res.data?.activities || [],
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load wishlist');
      setWishlistUser(null);
    } finally {
      setWishlistLoading(false);
    }
  };

  const closeUserWishlist = () => {
    setWishlistUser(null);
    setWishlistData({ items: [], activities: [] });
  };

  const fmtActivityDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const visibleUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) => [
      user.id,
      user.user_name,
      user.email,
      user.user_phone,
      user.preferred_city,
      user.preferred_state,
      user.preferred_category,
      user.preferred_property_type,
    ].some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [users, query]);

  return (
    <div className="admin-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Registered Users</h1>
          <p>All users who signed up on the platform</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={openCreateModal}
          style={{ cursor: 'pointer', position: 'relative', zIndex: 5 }}
        >
          + Add New User
        </button>
      </div>

      <div className="admin-list-search">
        <FiSearch aria-hidden="true" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, phone, or ID"
          aria-label="Search users"
        />
      </div>

      <div className="admin-card table-wrapper">
        {loading ? (
          <div className="skeleton" style={{ height: '300px', borderRadius: '8px' }}></div>
        ) : visibleUsers.length === 0 ? (
          <div className="empty-state">
            <FiInbox size={48} />
            <h3>No Users Found</h3>
            <p>{query.trim() ? 'No users match that search.' : 'Users who sign up on the platform, or that you add with Add New User, appear here.'}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-hybrid-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Preference</th>
                  <th>Language</th>
                  <th>Status</th>
                  <th>Access Chat</th>
                  <th>Listings</th>
                  <th>Wishlist</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map(u => {
                  const locationLabel = resolveLocation(u);
                  return (
                    <tr key={u.id}>
                      <td><UserAvatar user={u} onClick={() => openPhotoPreview(u)} /></td>
                      <td><span className="hybrid-id">#{u.id}</span></td>
                      <td><strong>{u.user_name}</strong></td>
                      <td>{u.email}</td>
                      <td>{u.user_phone || 'N/A'}</td>

                      {/* ── Preference — shows Type / Category / State / City ── */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '180px' }}>
                          <span style={{
                            alignSelf: 'flex-start',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            padding: '0.12rem 0.45rem',
                            borderRadius: '999px',
                            background: (u.preferred_property_type || u.preferred_category || u.preferred_state || u.preferred_city) ? '#dcfce7' : '#fee2e2',
                            color: (u.preferred_property_type || u.preferred_category || u.preferred_state || u.preferred_city) ? '#166534' : '#991b1b',
                          }}>
                            {(u.preferred_property_type || u.preferred_category || u.preferred_state || u.preferred_city) ? 'Set' : 'Not Set'}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', fontSize: '0.78rem', color: '#475569' }}>
                            <span><strong>Type:</strong> {u.preferred_property_type || '—'}</span>
                            <span><strong>Category:</strong> {u.preferred_category || '—'}</span>
                            <span><strong>State:</strong> {u.preferred_state || '—'}</span>
                            <span><strong>City:</strong> {u.preferred_city || '—'}</span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span style={{
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: u.preferred_locale ? '#0f766e' : '#94a3b8',
                        }}>
                          {resolveLocaleLabel(u.preferred_locale)}
                        </span>
                      </td>

                      <td>
                        <span className={`info-pill ${u.is_active ? '' : 'inactive-pill'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <label className="toggle-label" style={{ width: 'fit-content' }}>
                          <input
                            type="checkbox"
                            checked={u.chat_access !== false}
                            onChange={(e) => toggleChatAccess(u, e.target.checked)}
                            aria-label={`Access Chat for ${u.user_name || 'user'}`}
                          />
                          <div className="toggle-switch"></div>
                        </label>
                      </td>
                      <td>
                        <div className="user-listings-cell">
                          <strong>{u.propertyCount}</strong>
                          {u.propertyCount > 0 && (
                            <button
                              type="button"
                              className="btn-icon"
                              title="View properties"
                              onClick={() => openUserProperties(u)}
                            >
                              <FiEye />
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="user-listings-cell">
                          <strong>{u.wishlistCount ?? (Array.isArray(u.wishlist) ? u.wishlist.length : 0)}</strong>
                          {(u.wishlistCount > 0 || (Array.isArray(u.wishlist) && u.wishlist.length > 0)) && (
                            <button
                              type="button"
                              className="btn-icon"
                              title="View wishlist"
                              onClick={() => openUserWishlist(u)}
                            >
                              <FiHeart />
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'N/A'}
                      </td>

                      <td>
                        <div className="table-actions">
                          {u.is_active === false ? (
                            <button
                              type="button"
                              className="btn-primary"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.65rem', fontSize: '0.78rem' }}
                              title="Reactivate account"
                              onClick={() => handleToggleActive(u)}
                            >
                              <FiUserCheck /> Reactivate
                            </button>
                          ) : (
                            <button
                              className="btn-icon text-warning"
                              title="Deactivate account"
                              aria-label="Deactivate account"
                              onClick={() => handleToggleActive(u)}
                            >
                              <FiUserX />
                            </button>
                          )}
                          <button 
                            className="btn-icon" 
                            title="View"
                            onClick={() => setViewingUser(u)}
                          >
                            <FiEye />
                          </button>
                          <button 
                            className="btn-icon text-danger" 
                            title="Delete"
                            onClick={() => handleDelete(u.id)}
                          >
                            <FiTrash2 />
                          </button>
                          <button 
                            className="btn-icon" 
                            title="Edit"
                            onClick={() => openEditModal(u)}
                          >
                            <FiEdit2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── View User Modal ── */}
      {viewingUser && (
        <div 
          className="admin-modal-overlay animate-fade-in" 
          onClick={() => setViewingUser(null)}
        >
          <div 
            className="admin-modal" 
            onClick={e => e.stopPropagation()} 
            style={{ maxWidth: '600px' }}
          >
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="btn-icon" onClick={() => setViewingUser(null)} aria-label="Close"><FiX /></button>
            </div>

            <div className="modal-form">
              <div className="user-detail-hero">
                <UserAvatar user={viewingUser} size={72} onClick={() => openPhotoPreview(viewingUser)} />
                <div>
                  <h3 style={{ margin: 0 }}>{viewingUser.user_name}</h3>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>{viewingUser.email}</p>
                </div>
              </div>
              <div className="filter-box-grid" style={{ gap: '2rem' }}>
                
                <div>
                  <label>Name</label>
                  <p>{viewingUser.user_name}</p>
                </div>

                <div>
                  <label>Email</label>
                  <p>{viewingUser.email}</p>
                </div>

                <div>
                  <label>Phone</label>
                  <p>{viewingUser.user_phone || 'N/A'}</p>
                </div>

                {/* ── Preference details — Type / Category / State / City ── */}
                <div>
                  <label>Preference</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                    <div style={{ display: 'flex', gap: '6px', fontSize: '0.85rem' }}>
                      <strong>Type:</strong>
                      <span>{viewingUser.preferred_property_type || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', fontSize: '0.85rem' }}>
                      <strong>Category:</strong>
                      <span>{viewingUser.preferred_category || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', fontSize: '0.85rem' }}>
                      <strong>State:</strong>
                      <span>{viewingUser.preferred_state || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', fontSize: '0.85rem' }}>
                      <strong>City:</strong>
                      <span>{viewingUser.preferred_city || '—'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label>Language</label>
                  <p>{resolveLocaleLabel(viewingUser.preferred_locale)}</p>
                </div>

                <div>
                  <label>Status</label>
                  <p>
                    <span className={`info-pill ${viewingUser.is_active ? '' : 'inactive-pill'}`}>
                      {viewingUser.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>

                <div>
                  <label>Total Listings</label>
                  <p>{viewingUser.propertyCount}</p>
                </div>

                <div>
                  <label>Wishlist Items</label>
                  <p>{viewingUser.wishlistCount ?? (Array.isArray(viewingUser.wishlist) ? viewingUser.wishlist.length : 0)}</p>
                </div>

                <div>
                  <label>Joined Date</label>
                  <p>
                    {viewingUser.createdAt 
                      ? new Date(viewingUser.createdAt).toLocaleDateString() 
                      : 'N/A'}
                  </p>
                </div>

              </div>

              <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                {viewingUser.is_active === false && (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                      handleToggleActive(viewingUser);
                      setViewingUser(null);
                    }}
                  >
                    <FiUserCheck style={{ marginRight: 6 }} />
                    Reactivate Account
                  </button>
                )}
                <button className="btn-secondary" onClick={() => setViewingUser(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── User Properties Modal ── */}
      {propertiesUser && (
        <div
          className="admin-modal-overlay animate-fade-in"
          onClick={closeUserProperties}
        >
          <div
            className="admin-modal user-props-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>Properties by {propertiesUser.user_name}</h2>
                <p className="user-props-modal__sub">
                  {propertiesUser.propertyCount} listing{propertiesUser.propertyCount === 1 ? '' : 's'} created
                </p>
              </div>
              <button type="button" className="btn-icon" onClick={closeUserProperties} aria-label="Close"><FiX /></button>
            </div>

            <div className="user-props-modal__body">
              {propertiesLoading ? (
                <div className="skeleton" style={{ height: 180, borderRadius: 8 }} />
              ) : userProperties.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem 1rem' }}>
                  <FiHome size={36} />
                  <h3>No properties found</h3>
                  <p>This user has not created any listings yet.</p>
                </div>
              ) : (
                <div className="user-props-list">
                  {userProperties.map((item) => {
                    const status = STATUS_LABELS[item.status_id] || STATUS_LABELS[1];
                    const img = propertyImgUrl(item.thumbnail);
                    return (
                      <article key={item.id} className="user-props-card">
                        <div className="user-props-card__media">
                          {img ? (
                            <img src={img} alt="" />
                          ) : (
                            <div className="user-props-card__placeholder"><FiHome /></div>
                          )}
                        </div>
                        <div className="user-props-card__body">
                          <div className="user-props-card__head">
                            <div>
                              <span className="hybrid-id">#{item.property_id || item.id}</span>
                              <h3>{propertyTitle(item)}</h3>
                            </div>
                            <span
                              className="user-props-status"
                              style={{ color: status.color, background: status.bg }}
                            >
                              {status.label}
                            </span>
                          </div>
                          <p className="user-props-card__price">{formatPrice(item)}</p>
                          <div className="user-props-card__meta">
                            <span className="info-pill">
                              {item.is_project ? 'Project' : 'Property'}
                            </span>
                            <span className="info-pill">
                              {(item.listing_type || 'sale').charAt(0).toUpperCase() + (item.listing_type || 'sale').slice(1)}
                            </span>
                            {item.is_draft && <span className="info-pill inactive-pill">Draft</span>}
                            {item.is_urgent && <span className="info-pill">Urgent</span>}
                            {item.is_highlighted && <span className="info-pill">Highlighted</span>}
                          </div>
                          {(item.location || item.address) && (
                            <p className="user-props-card__location">
                              <FiMapPin size={13} />
                              {item.location || item.address}
                            </p>
                          )}
                          <p className="user-props-card__date">
                            Created {item.createdAt
                              ? new Date(item.createdAt).toLocaleDateString('en-IN', {
                                day: 'numeric', month: 'short', year: 'numeric',
                              })
                              : item.date || '—'}
                          </p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="user-props-modal__footer">
              <button type="button" className="btn-secondary" onClick={closeUserProperties}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── User Wishlist Modal ── */}
      {wishlistUser && (
        <div className="admin-modal-overlay animate-fade-in" onClick={closeUserWishlist}>
          <div className="admin-modal user-props-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Wishlist — {wishlistUser.user_name}</h2>
                <p className="user-props-modal__sub">
                  {wishlistData.items.length} saved item{wishlistData.items.length === 1 ? '' : 's'}
                </p>
              </div>
              <button type="button" className="btn-icon" onClick={closeUserWishlist} aria-label="Close"><FiX /></button>
            </div>

            <div className="user-props-modal__body">
              {wishlistLoading ? (
                <div className="skeleton" style={{ height: 180, borderRadius: 8 }} />
              ) : wishlistData.items.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem 1rem' }}>
                  <FiHeart size={36} />
                  <h3>No wishlist items</h3>
                  <p>This user has not saved any properties yet.</p>
                </div>
              ) : (
                <>
                  <div className="user-props-list">
                    {wishlistData.items.map((item) => {
                      const status = STATUS_LABELS[item.status_id] || STATUS_LABELS[1];
                      const img = propertyImgUrl(item.thumbnail);
                      return (
                        <article key={item.property_id} className="user-props-card">
                          <div className="user-props-card__media">
                            {img ? (
                              <img src={img} alt="" />
                            ) : (
                              <div className="user-props-card__placeholder"><FiHeart /></div>
                            )}
                          </div>
                          <div className="user-props-card__body">
                            <div className="user-props-card__head">
                              <div>
                                <span className="hybrid-id">#{item.property_id}</span>
                                <h3>{item.title}</h3>
                              </div>
                              {!item.missing && (
                                <span
                                  className="user-props-status"
                                  style={{ color: status.color, background: status.bg }}
                                >
                                  {status.label}
                                </span>
                              )}
                            </div>
                            {!item.missing && (
                              <>
                                <p className="user-props-card__price">{formatPrice(item)}</p>
                                <div className="user-props-card__meta">
                                  <span className="info-pill">{item.is_project ? 'Project' : 'Property'}</span>
                                  <span className="info-pill">
                                    {(item.listing_type || 'sale').charAt(0).toUpperCase() + (item.listing_type || 'sale').slice(1)}
                                  </span>
                                </div>
                                {item.location && (
                                  <p className="user-props-card__location">
                                    <FiMapPin size={13} />
                                    {item.location}
                                  </p>
                                )}
                              </>
                            )}
                            {item.missing && (
                              <p className="user-props-card__location">Property no longer available</p>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  {wishlistData.activities.length > 0 && (
                    <div className="user-wishlist-activity">
                      <h3>Wishlist Activity</h3>
                      <ul>
                        {wishlistData.activities.map((act, idx) => (
                          <li key={`${act.property_id}-${act.acted_at}-${idx}`}>
                            <span>{act.label}</span>
                            <time>{fmtActivityDate(act.acted_at)}</time>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="user-props-modal__footer">
              <button type="button" className="btn-secondary" onClick={closeUserWishlist}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit User Modal (portaled so admin layout overflow can't clip it) ── */}
      {isModalOpen && createPortal(
        <div
          className="admin-modal-overlay animate-fade-in"
          style={{ zIndex: 10050, background: 'rgba(15, 23, 42, 0.45)' }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="admin-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-form-modal-title"
          >
            <div className="modal-header">
              <h2 id="user-form-modal-title">{editingUser ? 'Edit User' : 'Add New User'}</h2>
              <button type="button" className="btn-icon" onClick={() => setIsModalOpen(false)} aria-label="Close"><FiX /></button>
            </div>
            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  required
                  value={formData.user_name}
                  onChange={e => setFormData({ ...formData, user_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={formData.user_phone}
                  onChange={e => setFormData({ ...formData, user_phone: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Password {editingUser && '(Leave blank to keep unchanged)'}</label>
                <input
                  type="password"
                  required={!editingUser}
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  autoComplete="new-password"
                />
              </div>

              <div style={{
                marginTop: '0.5rem',
                marginBottom: '0.75rem',
                padding: '0.85rem 1rem',
                borderRadius: 10,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', marginBottom: 10 }}>
                  Access & Permissions
                </div>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong style={{ display: 'block', fontSize: 14, color: '#0f172a' }}>Account active</strong>
                    <span style={{ fontSize: 12, color: '#64748b' }}>User can sign in on web and mobile</span>
                  </span>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.chat_access !== false}
                    onChange={e => setFormData({ ...formData, chat_access: e.target.checked })}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong style={{ display: 'block', fontSize: 14, color: '#0f172a' }}>Access Chat</strong>
                    <span style={{ fontSize: 12, color: '#64748b' }}>User can message admin from the website chat</span>
                  </span>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.can_post_listings !== false}
                    onChange={e => setFormData({ ...formData, can_post_listings: e.target.checked })}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong style={{ display: 'block', fontSize: 14, color: '#0f172a' }}>Can post listings</strong>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Allow creating properties and projects</span>
                  </span>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.skip_kyc}
                    onChange={e => setFormData({ ...formData, skip_kyc: e.target.checked })}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong style={{ display: 'block', fontSize: 14, color: '#0f172a' }}>Skip KYC verification</strong>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Bypass document KYC when posting listings</span>
                  </span>
                </label>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Default listing role</label>
                  <select
                    value={formData.default_listing_role || 'owner'}
                    onChange={e => setFormData({ ...formData, default_listing_role: e.target.value })}
                  >
                    <option value="owner">Owner</option>
                    <option value="agent">Agent / Broker</option>
                  </select>
                </div>

                {!editingUser && (
                  <p style={{ margin: '12px 0 0', fontSize: 12, color: '#0f766e', lineHeight: 1.45 }}>
                    Admin-created users are marked email-verified and can log in with their password on web or mobile — no OTP verification required.
                  </p>
                )}
              </div>

              <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary">{editingUser ? 'Update User' : 'Add User'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      )}

      {photoPreview && (
        <div
          className="admin-modal-overlay animate-fade-in user-photo-preview-overlay"
          onClick={closePhotoPreview}
        >
          <div
            className="admin-modal user-photo-preview-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-photo-preview-title"
          >
            <div className="modal-header">
              <div>
                <h2 id="user-photo-preview-title">Profile Photo</h2>
                <p className="user-photo-preview-sub">{photoPreview.user_name}</p>
              </div>
              <button
                type="button"
                className="btn-icon"
                onClick={closePhotoPreview}
                aria-label="Close preview"
              >
                <FiX />
              </button>
            </div>
            <div className="user-photo-preview-body">
              <img
                src={userAvatarUrl(photoPreview.profile)}
                alt={`${photoPreview.user_name} profile`}
                className="user-photo-preview-image"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
